import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { twJoin } from 'tailwind-merge';

import {
  HeadlessPopover,
  Icon,
  IconButton,
  RyeconArrowLeft,
  RyeconClose,
  RyeconMagnifyingGlass,
} from '@dbt-labs/sourdough';

import {
  AccessLevel,
  DbtAsset,
  SearchFieldType,
  WarehouseAsset,
} from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { getDefaultAccountFilters, useFilterContext } from '@/context/FilterProvider';
import { useNavigationHistory } from '@/context/NavigationHistoryProvider';
import { useAccountSearchResultsWrapper } from '@/hooks/discovery/accountSearchWrapper';
import { useLinkToExplorer } from '@/hooks/links';
import { AccountSearchResultItem } from '@/pages/account/search/components/AccountSearchResultItem';
import { AccountSearchResultDisplayData } from '@/pages/account/search/components/AccountSearchResultsList';
import { truthy } from '@/util/array';
import { MAXIMUM_QUERY_LENGTH, QUERY_TOO_LONG_MESSAGE } from '@/util/input';
import { toSearchResourceType } from '@/util/resourceType';

const MINIMUM_QUERY_LENGTH = 1;
const MAX_POPOVER_HEIGHT = 384; // matches max-h-96 (24rem)

const debouncedSetQuery = debounce(
  (setQuery: (value: string) => void, value: string) => {
    setQuery(value);
  },
  500,
);

export interface NewSearchInputParams {
  shouldUseTypeahead: boolean;
}

export const NewSearchInput: FC<NewSearchInputParams> = ({ shouldUseTypeahead }) => {
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  let { filters } = useFilterContext();
  filters =
    Object.keys(filters).length > 0
      ? filters
      : getDefaultAccountFilters(hasAccountFeature('catalogIngestion'));

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const searchResultsLink = useLinkToExplorer('summitSearchResults');
  const resourceDetailsLink = useLinkToExplorer('resourceDetails');
  const assetDetailsLink = useLinkToExplorer('catalogAsset');
  const [params] = useSearchParams();
  const rawSearchParam = params.get('search');
  const { navigateBack } = useNavigationHistory();

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [searchInput, setSearchInput] = useState(rawSearchParam ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState(rawSearchParam ?? '');
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  const [maxPopoverHeight, setMaxPopoverHeight] = useState<number>(MAX_POPOVER_HEIGHT);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listItemsRef = useRef<HTMLElement[]>([]);
  const listContentRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const isQueryTooLong = searchInput.length > MAXIMUM_QUERY_LENGTH;

  const searchInputRef = useRef(searchInput);
  searchInputRef.current = searchInput;

  const debounceQuery = useCallback(
    (value: string) => {
      debouncedSetQuery(setDebouncedQuery, value);
    },
    [setDebouncedQuery],
  );

  const searchQuery = useAccountSearchResultsWrapper({
    accountId,
    filter: {
      query: debouncedQuery,
      fields: [SearchFieldType.Name],
      resourceTypes:
        filters.resourceType?.map(toSearchResourceType).filter(truthy) || [],
      tags: filters.tags || [],
      materializationTypes: filters.materialization?.length
        ? filters.materialization
        : null,
      modelingLayers: filters.modelingLayer?.length ? filters.modelingLayer : null,
      accessLevels: filters.access?.length ? (filters.access as AccessLevel[]) : null,
      assetTypes: [...(filters.platforms || [])],
      dbtEnvs: filters.environmentTypes || null,
      projectIds: filters.projects || null,
      warehouseEntityTypes: filters.warehouseAssetTypes || null,
      urns: null,
      environmentIds: null,
      uniqueIds: null,
    },
    pageSize: 25,
    pause:
      !accountId ||
      !isInputFocused ||
      !shouldUseTypeahead ||
      isQueryTooLong ||
      debouncedQuery.length < MINIMUM_QUERY_LENGTH,
  });

  const searchResults = searchQuery.data as
    | AccountSearchResultDisplayData[]
    | undefined;
  const shouldShowPopover =
    !isQueryTooLong &&
    searchInput.length >= MINIMUM_QUERY_LENGTH &&
    debouncedQuery.length >= MINIMUM_QUERY_LENGTH &&
    (!searchQuery.fetching || !!searchResults?.length);

  useEffect(() => {
    setSearchInput(rawSearchParam ?? '');
    setDebouncedQuery(rawSearchParam ?? '');
  }, [rawSearchParam]);

  useEffect(() => {
    setHighlightedIndex(-1);
    setHoveredIndex(-1);
    listItemsRef.current = [];
  }, [searchResults]);

  useEffect(() => {
    if (
      shouldScroll &&
      highlightedIndex >= 0 &&
      listItemsRef.current[highlightedIndex] &&
      listContentRef.current
    ) {
      const highlightedItem = listItemsRef.current[highlightedIndex];
      const container = listContentRef.current;

      const itemTop = highlightedItem.offsetTop;
      const itemBottom = itemTop + highlightedItem.offsetHeight;
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.offsetHeight;

      if (itemTop < containerTop) {
        container.scrollTop = itemTop - 10;
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.offsetHeight + 10;
      }
      setShouldScroll(false);
    }
  }, [highlightedIndex, shouldScroll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isPopoverOpen &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        listContentRef.current &&
        !listContentRef.current.contains(event.target as Node) &&
        (!footerRef.current || !footerRef.current.contains(event.target as Node))
      ) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    // Hack to get around the popover stealing focus from the input
    if (isPopoverOpen && shouldShowPopover && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isPopoverOpen, shouldShowPopover]);

  // update trigger width and calculate available height when the popover opens
  useEffect(() => {
    const updatePopoverDimensions = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);

        // Calculate available vertical space below the trigger to prevent flip to side
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        // Leave some padding (16px) from the bottom of the viewport
        // Cap at the original max height (384px) so it doesn't grow unnecessarily
        const availableHeight = Math.min(
          Math.max(spaceBelow - 16, 100),
          MAX_POPOVER_HEIGHT,
        );
        setMaxPopoverHeight(availableHeight);
      }
    };

    if (isPopoverOpen) {
      updatePopoverDimensions();
      window.addEventListener('resize', updatePopoverDimensions);
    }

    return () => {
      window.removeEventListener('resize', updatePopoverDimensions);
    };
  }, [isPopoverOpen]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      isPopoverOpen &&
      shouldShowPopover &&
      (e.key === 'ArrowDown' || e.key === 'ArrowUp')
    ) {
      e.preventDefault();

      if (e.key === 'ArrowDown') {
        setShouldScroll(true);
        setHighlightedIndex(0);
      } else if (e.key === 'ArrowUp') {
        const itemCount = (searchResults?.length || 0) + 1;
        setShouldScroll(true);
        setHighlightedIndex(itemCount - 1);
      }
      return;
    }

    if (e.key === 'Escape' && isPopoverOpen) {
      e.preventDefault();
      setIsPopoverOpen(false);
      return;
    }

    if (e.key === 'Enter') {
      navigateToSearch();
    }
  };

  const handleItemRef = (index: number) => (el: HTMLElement | null) => {
    if (el) {
      listItemsRef.current[index] = el;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const itemCount = (searchResults?.length || 0) + 1; // +1 for "View all results" item

    if (!isPopoverOpen || !shouldShowPopover) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setShouldScroll(true);
        if (highlightedIndex < 0) {
          setHighlightedIndex(0);
        } else {
          const newIndex = highlightedIndex < itemCount - 1 ? highlightedIndex + 1 : 0;
          setHighlightedIndex(newIndex);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setShouldScroll(true);
        if (highlightedIndex < 0) {
          setHighlightedIndex(itemCount - 1);
        } else {
          const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : itemCount - 1;
          setHighlightedIndex(newIndex);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex === searchResults?.length) {
            navigateToSearch();
          } else if (searchResults && highlightedIndex < searchResults.length) {
            const selectedResult = searchResults[highlightedIndex];
            if (selectedResult) {
              listItemsRef.current[highlightedIndex]?.click();
              setIsPopoverOpen(false);
            }
          }
        } else {
          navigateToSearch();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsPopoverOpen(false);
        inputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const handleClearInput = () => {
    setSearchInput('');
    setDebouncedQuery('');
    inputRef.current?.focus();
  };

  const navigateToSearch = () => {
    if (isQueryTooLong) return;
    navigate(
      searchResultsLink({
        accountId: accountId!,
        search: searchInput,
        filters: JSON.stringify(filters),
      }),
    );
  };

  const navigateToDetails = (
    dbtAsset: DbtAsset | null,
    warehouseAsset: WarehouseAsset | null,
  ) => {
    if (!dbtAsset && !warehouseAsset) return;
    if (dbtAsset) {
      navigate(
        resourceDetailsLink({
          accountId: accountId!,
          projectId: dbtAsset.projectId,
          environmentType: dbtAsset.environmentDeploymentType ?? 'production',
          dbtUniqueId: dbtAsset.uniqueId,
        }),
      );
      return;
    }
    if (warehouseAsset) {
      navigate(
        assetDetailsLink({
          accountId: accountId!,
          assetId: warehouseAsset.urn,
        }),
      );
      return;
    }
  };

  const className = twJoin(
    'transition-color group flex h-8 overflow-hidden rounded-md border duration-100 motion-reduce:duration-0 bg-bgMain',
    isQueryTooLong
      ? 'border-borderDanger'
      : isInputFocused
        ? 'input-focused border-borderBrand'
        : 'border-borderMain',
  );

  return (
    <div
      aria-haspopup="true"
      aria-controls="searchMenu"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      className="m-2 w-1/3"
    >
      <HeadlessPopover
        isOpen={isPopoverOpen && shouldShowPopover}
        onOpenChange={setIsPopoverOpen}
        placement="bottom-start"
        labelledBy="search-popover"
        testId="search-popover"
      >
        <HeadlessPopover.Trigger asChild>
          <div className={className} ref={triggerRef}>
            <span className="flex-0 flex items-center pl-2 text-sm text-fgMain ">
              {pathname && pathname.includes('search') ? (
                <IconButton ryecon={RyeconArrowLeft} size="md" onClick={navigateBack} />
              ) : (
                <Icon ryecon={RyeconMagnifyingGlass} />
              )}
            </span>
            <span className="flex flex-1 overflow-hidden rounded-md bg-bgMain">
              <input
                name="search-input"
                data-testid="new-search-input"
                ref={inputRef}
                value={searchInput}
                placeholder={'Search for assets across your account'}
                onFocus={() => {
                  setIsInputFocused(true);
                }}
                onClick={(e: any) => {
                  setIsPopoverOpen(e.target.value.length >= MINIMUM_QUERY_LENGTH);
                }}
                onChange={(e: any) => {
                  const input: string = e.target.value;
                  setSearchInput(input);
                  debounceQuery(input);
                  setIsPopoverOpen(input.length >= MINIMUM_QUERY_LENGTH);
                }}
                onKeyDown={handleInputKeyDown}
                className="font-body w-full flex-1 items-center border-transparent bg-bgMain bg-none px-2 placeholder-fgDecorative focus:border-transparent focus:outline-none focus:ring-0"
              />
              {searchInput && (
                <span className="flex-0 flex items-center pr-2 text-sm text-fgMain">
                  <IconButton
                    ryecon={RyeconClose}
                    size="sm"
                    onClick={handleClearInput}
                    data-testid="clear-search"
                  />
                </span>
              )}
            </span>
          </div>
        </HeadlessPopover.Trigger>
        {shouldUseTypeahead && (
          <HeadlessPopover.Content
            className="z-50 mt-1 w-full rounded border border-borderMuted bg-bgMain py-1 shadow-hover"
            testId="search-results-content"
            style={triggerWidth ? { width: `${triggerWidth}px` } : undefined}
          >
            <div
              className="flex flex-col overflow-hidden"
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              style={{ maxHeight: `${maxPopoverHeight}px` }}
            >
              <div
                ref={listContentRef}
                className="flex-1 overflow-auto py-1"
                onClick={() => {
                  setTimeout(() => {
                    setIsInputFocused(false);
                    setIsPopoverOpen(false);
                  }, 200);
                }}
              >
                {searchQuery.fetching ? (
                  <div className="px-3 py-2 text-sm text-fgDecorative">
                    Loading results...
                  </div>
                ) : searchResults && searchResults.length > 0 ? (
                  <>
                    {searchResults.map((result, index) => (
                      <div
                        key={`${index}`}
                        ref={handleItemRef(index)}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(-1)}
                        onClick={() =>
                          navigateToDetails(
                            result.hit.dbtAsset,
                            result.hit.warehouseAsset,
                          )
                        }
                        className={twJoin(
                          'flex w-full cursor-pointer items-start border-b border-borderMuted bg-bgMain px-3 py-2 text-sm text-fgMain outline-none focus:outline-none ',
                          highlightedIndex === index || hoveredIndex === index
                            ? 'bg-bgMainHover'
                            : '',
                        )}
                      >
                        <AccountSearchResultItem
                          data={result}
                          query={searchInput}
                          testId={`search-dropdown-result-${index}`}
                          mini={true}
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-fgDecorative">
                    No results found
                  </div>
                )}
              </div>
              {!searchQuery.fetching && searchResults && searchResults.length > 0 && (
                <div
                  ref={(el) => {
                    footerRef.current = el;
                    handleItemRef(searchResults.length)(el);
                  }}
                  onMouseEnter={() => setHoveredIndex(searchResults.length)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  onClick={navigateToSearch}
                  className={twJoin(
                    'flex w-full cursor-pointer items-center justify-center border-b border-t border-borderMuted bg-bgMain px-3 py-2 text-sm font-semibold text-fgMain outline-none focus:outline-none',
                    highlightedIndex === searchResults.length ||
                      hoveredIndex === searchResults.length
                      ? 'bg-bgMainHover'
                      : '',
                  )}
                  data-testid="view-all-results-footer"
                >
                  View all results
                </div>
              )}
            </div>
          </HeadlessPopover.Content>
        )}
      </HeadlessPopover>
      {isQueryTooLong && (
        <p className="mt-1 text-xs text-fgDanger">{QUERY_TOO_LONG_MESSAGE}</p>
      )}
    </div>
  );
};
